import { Box, Typography } from '@mui/material';
import { formatDate } from '@utils/utils';

interface NoteItemProps {
  title: string;
  patientName?: string;
  timestamp: number;
  summary: string;
  onClick: () => void;
  isFirst?: boolean;
}

/**
 * Pure UI component for displaying a single note item
 * Shows title, patient name, timestamp, and summary preview
 */
export const NoteItem: React.FC<NoteItemProps> = ({
  title,
  patientName,
  timestamp,
  summary,
  onClick,
  isFirst = false,
}) => {
  return (
    <Box
      onClick={onClick}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
        px: 3,
        py: 2,
        borderTop: isFirst ? 'none' : '1px solid',
        borderColor: 'borderColors.primary',
        cursor: 'pointer',
        transition: 'background-color 0.2s',
        position: 'relative',
        '&:hover': {
          backgroundColor: 'rgba(255, 255, 255, 0.5)',
        },
      }}
    >
      {/* Header row with title and timestamp */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
        {/* Title section */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {/* Primary text (Title or Patient Name) */}
          <Typography
            variant="body1"
            sx={{
              fontWeight: 600,
              color: 'text.primary',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {title || patientName || 'Untitled Note'}
          </Typography>

          {/* Secondary text (Patient name if title exists) */}
          {title && patientName && (
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                fontSize: '0.875rem',
                fontWeight: 600,
              }}
            >
              {patientName}
            </Typography>
          )}
        </Box>

        {/* Timestamp - positioned to the right */}
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
            fontSize: '0.75rem',
            fontWeight: 600,
            flexShrink: 0,
          }}
        >
          {formatDate(timestamp)}
        </Typography>
      </Box>

      {/* Summary preview */}
      <Typography
        variant="body2"
        sx={{
          color: 'text.secondary',
          overflow: 'hidden',
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          lineHeight: 1.4,
          maxHeight: '2.8em', // Fallback for older browsers (2 lines × 1.4 line-height)
          fontSize: '0.875rem',
        }}
      >
        {summary}
      </Typography>
    </Box>
  );
};